import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApiCacheRefresh } from '../lib/apiCache';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Search, Eye, ChevronRight, MapPin, PlusCircle, X, Target, CheckCircle, XCircle, Phone, Clock, CalendarClock, Hourglass, RotateCcw, User } from 'lucide-react';
import { formatDatePL } from '../components/DatePickerField';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LEAD_STATUSES = {
  lead: { label: 'Nowy', color: 'bg-blue-600 text-[var(--crm-text-primary)]' },
  oczekujacy: { label: 'Oczekujący', color: 'bg-indigo-500 text-[var(--crm-text-primary)]' },
  brak_akceptacji: { label: 'Odrzucony', color: 'bg-slate-500 text-[var(--crm-text-primary)]' },
};

const STATUS_ACTION_LABELS = {
  akceptacja: { title: 'Akceptuj leada', desc: 'Lead zostanie przeniesiony do aktywnych zleceń (status: Akceptacja). Czy kontynuować?' },
  oczekujacy: { title: 'Oznacz jako oczekujący', desc: 'Lead zostanie oznaczony jako oczekujący na odpowiedź klienta. Czy kontynuować?' },
  brak_akceptacji: { title: 'Odrzuć leada', desc: 'Lead zostanie oznaczony jako odrzucony. Możesz go przywrócić później. Czy kontynuować?' },
  lead: { title: 'Przywróć leada', desc: 'Lead zostanie przywrócony do statusu "Nowy". Czy kontynuować?' },
};

// Per-user persistence of the Leads filter/sort choice (stored in the browser)
const getLeadUserId = () => {
  try {
    const u = JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || 'null');
    return u?.id || 'anon';
  } catch { return 'anon'; }
};
const readLeadPref = (key, fallback) => {
  try {
    return localStorage.getItem(`leads_${key}_${getLeadUserId()}`) || fallback;
  } catch { return fallback; }
};
const writeLeadPref = (key, value) => {
  try { localStorage.setItem(`leads_${key}_${getLeadUserId()}`, value); } catch { /* ignore */ }
};


export const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => readLeadPref('status', 'all'));
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [sortBy, setSortBy] = useState(() => readLeadPref('sort', 'newest'));
  const [creators, setCreators] = useState([]);
  const [fabOpen, setFabOpen] = useState(false);
  const [editFollowUp, setEditFollowUp] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { id, status }
  const [rejectReason, setRejectReason] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();

  const canSeeAll = user?.available_features?.includes('leads_all') || false;

  // Persist the user's filter/sort choice per user (remembered across visits)
  useEffect(() => { writeLeadPref('status', statusFilter); }, [statusFilter]);
  useEffect(() => { writeLeadPref('sort', sortBy); }, [sortBy]);


  useEffect(() => { fetchLeads(); }, [ownerFilter]);

  useEffect(() => {
    if (canSeeAll) {
      axios.get(`${API_URL}/projects/lead-creators`).then(res => setCreators(res.data)).catch(() => {});
    }
  }, [canSeeAll]);

  useEffect(() => {
    let result = leads;
    if (statusFilter === 'all') {
      result = result.filter(p => p.status !== 'brak_akceptacji');
    } else {
      result = result.filter(p => p.status === statusFilter);
    }
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.nazwa?.toLowerCase().includes(t) || p.klient?.toLowerCase().includes(t) ||
        p.telefon_klienta?.toLowerCase().includes(t) || p.adres_inwestycji?.toLowerCase().includes(t) ||
        p.source?.toLowerCase().includes(t) || p.created_by?.toLowerCase().includes(t)
      );
    }
    result.sort((a, b) => {
      if (sortBy === 'newest') {
        return (b.created_at || '').localeCompare(a.created_at || '');
      }
      if (sortBy === 'activity') {
        return (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '');
      }
      if (sortBy === 'reminder') {
        const al = a.reminder_level || 0, bl = b.reminder_level || 0;
        if (bl !== al) return bl - al;
        const af2 = a.follow_up_date || '', bf2 = b.follow_up_date || '';
        if (af2 && bf2) return af2.localeCompare(bf2);
        if (af2) return -1;
        if (bf2) return 1;
        return (b.created_at || '').localeCompare(a.created_at || '');
      }
      // sortBy === 'follow_up'
      const af = a.follow_up_date || '', bf = b.follow_up_date || '';
      if (af && bf) return af.localeCompare(bf);
      if (af) return -1;
      if (bf) return 1;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    setFiltered(result);
  }, [leads, searchTerm, statusFilter, sortBy]);

  useApiCacheRefresh(() => fetchLeads());

  const fetchLeads = async () => {
    try {
      const params = { include_archived: true };
      if (ownerFilter !== 'all') params.owner_filter = ownerFilter;
      const res = await axios.get(`${API_URL}/projects`, { params });
      setLeads(res.data.filter(p => ['lead', 'oczekujacy', 'brak_akceptacji'].includes(p.status)));
    } catch { toast.error('Błąd pobierania leadów'); }
    finally { setLoading(false); }
  };

  const askChangeStatus = (id, newStatus) => setConfirmDialog({ id, status: newStatus });

  const confirmChangeStatus = async () => {
    if (!confirmDialog) return;
    const isReject = confirmDialog.status === 'brak_akceptacji';
    if (isReject && !rejectReason.trim()) {
      toast.error('Musisz podać przyczynę odrzucenia leada.');
      return;
    }
    try {
      const payload = { status: confirmDialog.status };
      if (isReject) payload.rejection_reason = rejectReason.trim();
      await axios.patch(`${API_URL}/projects/${confirmDialog.id}`, payload);
      const s = confirmDialog.status;
      toast.success(
        s === 'akceptacja' ? 'Lead zaakceptowany — przeniesiony do Zleceń' :
        s === 'oczekujacy' ? 'Oznaczono jako oczekujący' :
        s === 'lead' ? 'Lead przywrócony' : 'Lead odrzucony'
      );
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd'); }
    finally { setConfirmDialog(null); setRejectReason(''); }
  };

  const saveFollowUp = async (id, date) => {
    try {
      await axios.patch(`${API_URL}/projects/${id}`, { follow_up_date: date || null, status: date ? 'oczekujacy' : 'lead' });
      toast.success(date ? `Przypomnienie: ${formatDatePL(date)}` : 'Przypomnienie usunięte');
      setEditFollowUp(null);
      fetchLeads();
    } catch { toast.error('Błąd zapisu'); }
  };

  const daysSince = (d) => d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null;

  const getFollowUpInfo = (lead) => {
    if (!lead.follow_up_date) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const fu = new Date(lead.follow_up_date); fu.setHours(0, 0, 0, 0);
    const diff = Math.ceil((fu - today) / 86400000);
    if (diff < 0) return { label: `${Math.abs(diff)} dni po terminie`, cls: 'text-red-400 bg-red-500/10 border-red-500/30' };
    if (diff === 0) return { label: 'Dziś!', cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30' };
    if (diff <= 2) return { label: `Za ${diff} dni`, cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30' };
    return { label: `Za ${diff} dni`, cls: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/30' };
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-full"><div className="text-[var(--crm-text-primary)]">Ładowanie...</div></div></Layout>;

  const dialogInfo = confirmDialog ? STATUS_ACTION_LABELS[confirmDialog.status] || {} : {};

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight" data-testid="leads-page-title">Leady</h1>
            <p className="text-[var(--crm-text-secondary)] mt-1 text-sm" data-testid="leads-count">{({ all: 'Aktywne', lead: 'Nowe', oczekujacy: 'Oczekujące', brak_akceptacji: 'Odrzucone' })[statusFilter]}: {filtered.length}</p>
          </div>
          <div className="hidden sm:flex gap-2">
            <Link to="/lead-terenowy">
              <Button data-testid="add-lead-field-btn" className="bg-[#F59E0B] hover:bg-[#D97706] text-[var(--crm-text-primary)]">
                <MapPin className="w-4 h-4 mr-2" />Dodaj z terenu
              </Button>
            </Link>
            <Link to="/projects/new?status=lead">
              <Button data-testid="add-lead-office-btn" className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
                <PlusCircle className="w-4 h-4 mr-2" />Dodaj z biura
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--crm-text-secondary)]" />
              <Input placeholder="Szukaj: klient, adres, telefon, źródło..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} data-testid="search-leads-input"
                className="pl-10 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="filter-lead-status" className="w-full sm:w-48 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                <SelectItem value="all">Wszystkie aktywne</SelectItem>
                <SelectItem value="lead">Nowe</SelectItem>
                <SelectItem value="oczekujacy">Oczekujące</SelectItem>
                <SelectItem value="brak_akceptacji">Odrzucone</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="sort-leads" className="w-full sm:w-52 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                <SelectValue placeholder="Sortuj" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                <SelectItem value="newest">Najnowsze (data utworzenia)</SelectItem>
                <SelectItem value="activity">Ostatnia aktywność</SelectItem>
                <SelectItem value="follow_up">Termin follow-up</SelectItem>
                <SelectItem value="reminder">Przypomnienie (poziom)</SelectItem>
              </SelectContent>
            </Select>
            {canSeeAll && creators.length > 0 && (
              <Select value={ownerFilter} onValueChange={v => { setOwnerFilter(v); setLoading(true); }}>
                <SelectTrigger data-testid="filter-lead-owner" className="w-full sm:w-48 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                  <User className="w-4 h-4 mr-1.5 text-[var(--crm-text-secondary)]" />
                  <SelectValue placeholder="Czyje leady" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                  <SelectItem value="all">Wszystkich</SelectItem>
                  {creators.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* ==================== MOBILE CARDS ==================== */}
        <div className="md:hidden space-y-3">
          {filtered.length === 0 ? (
            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-8 text-center">
              <Target className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-[var(--crm-text-secondary)]">Brak leadów</p>
            </Card>
          ) : (
            filtered.map((lead) => {
              const days = daysSince(lead.created_at);
              const isStale = days !== null && days > 5;
              const fuInfo = getFollowUpInfo(lead);
              const sb = LEAD_STATUSES[lead.status] || LEAD_STATUSES.lead;
              return (
                <Card key={lead.id} data-testid={`lead-card-${lead.id}`}
                  className={`bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 ${isStale && lead.status !== 'brak_akceptacji' && !lead.follow_up_date ? 'border-l-4 border-l-amber-500' : ''}`}>
                  <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={() => navigate(`/projects/${lead.id}`)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Badge className={sb.color + ' text-[10px]'}>{sb.label}</Badge>
                        {fuInfo && <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${fuInfo.cls}`}><CalendarClock className="w-3 h-3 inline mr-0.5" />{fuInfo.label}</span>}
                        {days !== null && <span className={`text-[10px] ${isStale ? 'text-amber-400' : 'text-[var(--crm-text-muted)]'}`}><Clock className="w-3 h-3 inline mr-0.5" />{days} dni</span>}
                      </div>
                      <h3 className="text-[var(--crm-text-primary)] font-semibold truncate">{lead.klient}</h3>
                      <p className="text-[var(--crm-text-secondary)] text-sm truncate">{lead.nazwa}</p>
                      {lead.telefon_klienta && (
                        <a href={`tel:${lead.telefon_klienta}`} onClick={e => e.stopPropagation()} className="text-[#FF6B35] text-sm flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />{lead.telefon_klienta}
                        </a>
                      )}
                      {lead.adres_inwestycji && <p className="text-[var(--crm-text-muted)] text-xs mt-1 truncate">{lead.adres_inwestycji}</p>}
                      {lead.source && <Badge className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-secondary)] text-[10px] mt-1.5">{lead.source}</Badge>}
                      {lead.created_by && (
                        <span className="text-[var(--crm-text-muted)] text-[10px] ml-1.5 mt-1.5 inline-flex items-center gap-1">
                          <User className="w-3 h-3" />{lead.created_by}
                        </span>
                      )}
                      {lead.total_offer > 0 && (
                        <span className="text-[var(--crm-text-primary)] font-mono text-sm font-medium ml-2 mt-1.5 inline-block">{(lead.total_offer / 1000).toFixed(0)}k zł</span>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-[var(--crm-text-secondary)] flex-shrink-0 mt-1" />
                  </div>

                  {/* Follow-up — not for rejected */}
                  {lead.status !== 'brak_akceptacji' && (
                    <div className="mt-3 pt-3 border-t border-[var(--crm-border)]/60">
                      {editFollowUp?.id === lead.id ? (
                        <div className="flex gap-2 items-center">
                          <Input type="date" value={editFollowUp.date} onChange={(e) => setEditFollowUp({ ...editFollowUp, date: e.target.value })}
                            className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] text-sm h-9 flex-1" />
                          <Button size="sm" onClick={() => saveFollowUp(lead.id, editFollowUp.date)} className="bg-indigo-600 hover:bg-indigo-700 text-[var(--crm-text-primary)] h-9 px-3 text-xs">Zapisz</Button>
                          <Button size="sm" onClick={() => setEditFollowUp(null)} className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)] h-9 w-9 p-0"><X className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <Button size="sm" onClick={() => setEditFollowUp({ id: lead.id, date: lead.follow_up_date || '' })} data-testid={`lead-followup-${lead.id}`}
                          className="w-full h-9 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 border border-indigo-500/30 text-xs">
                          <CalendarClock className="w-3.5 h-3.5 mr-1.5" />{lead.follow_up_date ? formatDatePL(lead.follow_up_date) : 'Ustaw przypomnienie'}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 mt-2">
                    {lead.status === 'brak_akceptacji' ? (
                      <Button size="sm" onClick={() => askChangeStatus(lead.id, 'lead')} data-testid={`lead-restore-${lead.id}`}
                        className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-[var(--crm-text-primary)] text-xs"><RotateCcw className="w-3.5 h-3.5 mr-1" />Przywróć</Button>
                    ) : (
                      <>
                        {lead.status === 'lead' && (
                          <Button size="sm" onClick={() => askChangeStatus(lead.id, 'oczekujacy')}
                            className="flex-1 h-9 bg-indigo-600 hover:bg-indigo-700 text-[var(--crm-text-primary)] text-xs"><Hourglass className="w-3.5 h-3.5 mr-1" />Oczekujący</Button>
                        )}
                        <Button size="sm" onClick={() => askChangeStatus(lead.id, 'akceptacja')} data-testid={`lead-accept-${lead.id}`}
                          className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"><CheckCircle className="w-3.5 h-3.5 mr-1" />Akceptuj</Button>
                        <Button size="sm" onClick={() => askChangeStatus(lead.id, 'brak_akceptacji')} data-testid={`lead-reject-${lead.id}`}
                          className="flex-1 h-9 bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] text-xs"><XCircle className="w-3.5 h-3.5 mr-1" />Odrzuć</Button>
                      </>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* ==================== DESKTOP TABLE ==================== */}
        <div className="hidden md:block bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[var(--crm-bg-panel)] border-b border-[var(--crm-border)] hover:bg-[var(--crm-bg-panel)]">
                <TableHead className="text-[var(--crm-text-secondary)] font-bold w-10">#</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold w-24 whitespace-nowrap">Status</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold min-w-[140px]">Klient</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold whitespace-nowrap">Telefon</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Adres</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold whitespace-nowrap">Kwota</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold whitespace-nowrap">Źródło</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold whitespace-nowrap">Założył</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold whitespace-nowrap">Przypomnienie</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold whitespace-nowrap">Czas w lejku</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold w-28 text-center">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-[var(--crm-text-secondary)] py-8">Brak leadów</TableCell></TableRow>
              ) : (
                filtered.map((lead, i) => {
                  const days = daysSince(lead.created_at);
                  const isStale = days !== null && days > 5;
                  const fuInfo = getFollowUpInfo(lead);
                  const sb = LEAD_STATUSES[lead.status] || LEAD_STATUSES.lead;
                  return (
                    <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}
                      className={`border-b border-[var(--crm-border)] hover:bg-[var(--crm-bg-card-hover)] ${isStale && lead.status !== 'brak_akceptacji' && !lead.follow_up_date ? 'bg-amber-500/5' : ''}`}>
                      <TableCell className="text-[#FF6B35] font-bold">{i + 1}</TableCell>
                      <TableCell className="whitespace-nowrap"><Badge className={sb.color}>{sb.label}</Badge></TableCell>
                      <TableCell className="text-[var(--crm-text-primary)] font-medium">
                        <div className="truncate max-w-[180px]" title={lead.klient}>{lead.klient}</div>
                        <div className="truncate max-w-[180px] text-[var(--crm-text-muted)] text-xs">{lead.nazwa}</div>
                        {lead.status === 'brak_akceptacji' && lead.rejection_reason && (
                          <div className="mt-1 text-xs text-red-400 max-w-[220px] whitespace-normal" title={lead.rejection_reason} data-testid={`reject-reason-${lead.id}`}>
                            Przyczyna: {lead.rejection_reason}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {lead.telefon_klienta ? (
                          <a href={`tel:${lead.telefon_klienta}`} className="text-[#FF6B35] hover:underline text-sm">{lead.telefon_klienta}</a>
                        ) : <span className="text-[var(--crm-text-muted)]">—</span>}
                      </TableCell>
                      <TableCell>
                        <div className="truncate max-w-[200px] text-[var(--crm-text-secondary)] text-sm" title={lead.adres_inwestycji}>{lead.adres_inwestycji || '—'}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {lead.total_offer > 0 ? (
                          <span className="text-[var(--crm-text-primary)] font-mono text-sm font-medium">{(lead.total_offer / 1000).toFixed(0)}k zł</span>
                        ) : <span className="text-[var(--crm-text-muted)] text-sm">—</span>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {lead.source ? <Badge className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-secondary)] text-[10px]">{lead.source}</Badge> : <span className="text-[var(--crm-text-muted)]">—</span>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-[var(--crm-text-secondary)] text-xs">
                        {lead.created_by || '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {lead.status === 'brak_akceptacji' ? (
                          <span className="text-[var(--crm-text-muted)] text-xs">—</span>
                        ) : editFollowUp?.id === lead.id ? (
                          <div className="flex gap-1 items-center">
                            <Input type="date" value={editFollowUp.date} onChange={(e) => setEditFollowUp({ ...editFollowUp, date: e.target.value })}
                              className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] text-xs h-7 w-32" />
                            <Button size="sm" onClick={() => saveFollowUp(lead.id, editFollowUp.date)} className="bg-indigo-600 text-[var(--crm-text-primary)] h-7 px-2 text-[10px]">OK</Button>
                            <Button size="sm" onClick={() => setEditFollowUp(null)} className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)] h-7 w-7 p-0"><X className="w-3 h-3" /></Button>
                          </div>
                        ) : (
                          <button onClick={() => setEditFollowUp({ id: lead.id, date: lead.follow_up_date || '' })} data-testid={`lead-followup-desktop-${lead.id}`}
                            className={`text-xs px-2 py-1 rounded border transition-colors ${fuInfo ? fuInfo.cls + ' font-semibold' : 'text-[var(--crm-text-muted)] border-[var(--crm-border)] hover:border-indigo-500 hover:text-indigo-400'}`}>
                            <CalendarClock className="w-3 h-3 inline mr-1" />{fuInfo ? fuInfo.label : 'Ustaw'}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className={`text-sm font-mono ${isStale && lead.status !== 'brak_akceptacji' ? 'text-amber-400 font-bold' : 'text-[var(--crm-text-secondary)]'}`}>
                          {days !== null ? `${days} dni` : '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" onClick={() => navigate(`/projects/${lead.id}`)} data-testid={`view-lead-${lead.id}`}
                            title="Zobacz" className="bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-primary)] h-7 w-7 p-0"><Eye className="w-3.5 h-3.5" /></Button>
                          {lead.status === 'brak_akceptacji' ? (
                            <Button size="sm" onClick={() => askChangeStatus(lead.id, 'lead')} data-testid={`restore-lead-${lead.id}`}
                              title="Przywróć" className="bg-blue-600 hover:bg-blue-700 text-[var(--crm-text-primary)] h-7 w-7 p-0"><RotateCcw className="w-3.5 h-3.5" /></Button>
                          ) : (
                            <>
                              {lead.status === 'lead' && (
                                <Button size="sm" onClick={() => askChangeStatus(lead.id, 'oczekujacy')}
                                  title="Oczekujący" className="bg-indigo-600 hover:bg-indigo-700 text-[var(--crm-text-primary)] h-7 w-7 p-0"><Hourglass className="w-3.5 h-3.5" /></Button>
                              )}
                              <Button size="sm" onClick={() => askChangeStatus(lead.id, 'akceptacja')} data-testid={`accept-lead-${lead.id}`}
                                title="Akceptuj" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 w-7 p-0"><CheckCircle className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" onClick={() => askChangeStatus(lead.id, 'brak_akceptacji')} data-testid={`reject-lead-${lead.id}`}
                                title="Odrzuć" className="bg-slate-600 hover:bg-slate-700 text-[var(--crm-text-primary)] h-7 w-7 p-0"><XCircle className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile FAB Speed Dial */}
      <div className="fixed bottom-24 right-4 z-50 sm:hidden" data-testid="lead-fab-container">
        {fabOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setFabOpen(false)} />
            <div className="absolute bottom-16 right-0 z-50 flex flex-col gap-3 items-end">
              <Link to="/lead-terenowy" onClick={() => setFabOpen(false)}>
                <div className="flex items-center gap-3">
                  <span className="bg-[var(--crm-bg-main)] text-[var(--crm-text-primary)] text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg border border-[var(--crm-border)]">Z terenu</span>
                  <div className="w-12 h-12 rounded-full bg-[#F59E0B] shadow-lg flex items-center justify-center" data-testid="fab-field-lead"><MapPin className="w-6 h-6 text-[var(--crm-text-primary)]" /></div>
                </div>
              </Link>
              <Link to="/projects/new?status=lead" onClick={() => setFabOpen(false)}>
                <div className="flex items-center gap-3">
                  <span className="bg-[var(--crm-bg-main)] text-[var(--crm-text-primary)] text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg border border-[var(--crm-border)]">Z biura</span>
                  <div className="w-12 h-12 rounded-full bg-[#FF6B35] shadow-lg flex items-center justify-center" data-testid="fab-office-lead"><PlusCircle className="w-6 h-6 text-white" /></div>
                </div>
              </Link>
            </div>
          </>
        )}
        <button onClick={() => setFabOpen(!fabOpen)} data-testid="lead-fab-button"
          className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all ${fabOpen ? 'bg-slate-600 rotate-45' : 'bg-[#FF6B35]'}`}>
          {fabOpen ? <X className="w-7 h-7 text-[var(--crm-text-primary)]" /> : <PlusCircle className="w-7 h-7 text-[var(--crm-text-primary)]" />}
        </button>
      </div>

      {/* Confirm Status Change Dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) { setConfirmDialog(null); setRejectReason(''); } }}>
        <AlertDialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" data-testid="status-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--crm-text-primary)]">{dialogInfo.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--crm-text-secondary)]">{dialogInfo.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          {confirmDialog?.status === 'brak_akceptacji' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-[var(--crm-text-primary)]">Przyczyna odrzucenia <span className="text-red-500">*</span></label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Np. za drogo, klient wybrał konkurencję, brak kontaktu..."
                rows={3}
                data-testid="reject-reason-input"
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)] border-[var(--crm-border)] hover:bg-[var(--crm-bg-card-hover)]" data-testid="status-confirm-cancel">Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={confirmChangeStatus} data-testid="status-confirm-ok"
              disabled={confirmDialog?.status === 'brak_akceptacji' && !rejectReason.trim()}
              className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white disabled:opacity-50 disabled:cursor-not-allowed">Potwierdź</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};
